import { useEffect, useRef, useState } from "react";
import css from "./Request.module.css";

const isPastTimeout = (createdAt, timeoutSeconds) =>
  !!createdAt &&
  !!timeoutSeconds &&
  Date.now() - createdAt >= timeoutSeconds * 1000;

const Request = ({ onComplete, request, selfDestruct, style }) => {
  const { id, createdAt, loadSize, networkSpeed, status, timeoutSeconds } =
    request;

  const [phase, setPhase] = useState("");
  const [timedOut, setTimedOut] = useState(() =>
    isPastTimeout(createdAt, timeoutSeconds),
  );

  const mainDomRef = useRef(null);
  const circleDomRef = useRef(null);
  const fillDomRef = useRef(null);
  const radialDomRef = useRef(null);

  const processAnimationRef = useRef(null);
  const slideAnimationRef = useRef(null);
  const timerAnimationRef = useRef(null);
  const timeoutIdsRef = useRef([]);

  const addTimeout = (fn, ms) => {
    const timeoutId = setTimeout(fn, ms);
    timeoutIdsRef.current.push(timeoutId);
    return timeoutId;
  };

  // Keep timedOut in sync with wall-clock time on every relevant change,
  // not just from the fill animation's finished callback. This ensures
  // status transitions (e.g. into "server-processed") that unmount the
  // fill element before its animation resolves still reflect timeout.
  useEffect(() => {
    if (isPastTimeout(createdAt, timeoutSeconds)) {
      setTimedOut(true);
    }
  }, [status, createdAt, timeoutSeconds]);

  // FILL — derives progress AND timedOut purely from request.createdAt,
  // so nothing needs to be written back onto the request.
  useEffect(() => {
    const el = fillDomRef.current;
    if (!el || !timeoutSeconds || !createdAt) return;

    const totalMs = timeoutSeconds * 1000;
    const elapsedMs = Date.now() - createdAt;
    const remainingMs = Math.max(totalMs - elapsedMs, 0);

    if (remainingMs === 0) {
      setTimedOut(true);
      return;
    }

    const progress = Math.min(elapsedMs / totalMs, 1);
    const currentScale = Math.max(1 - progress, 0);

    timerAnimationRef.current = el.animate(
      [{ transform: `scaleY(${currentScale})` }, { transform: "scaleY(0)" }],
      {
        duration: remainingMs,
        easing: "linear",
        fill: "forwards",
      },
    );

    timerAnimationRef.current.finished
      .then(() => {
        setTimedOut(true);
      })
      .catch(() => {});

    return () => timerAnimationRef.current?.cancel();
  }, [id, createdAt, timeoutSeconds]);

  // TRACK
  useEffect(() => {
    const el = mainDomRef.current;
    if (
      !el ||
      !["client-queue", "client-server", "queue-server"].includes(status)
    )
      return;
    slideAnimationRef.current = el.animate([{ right: "100%" }, { right: 0 }], {
      duration: networkSpeed,
      easing: "linear",
      fill: "forwards",
    });

    slideAnimationRef.current.finished
      .then(() => {
        onComplete?.(request);
      })
      .catch(() => {});

    return () => slideAnimationRef.current?.cancel();
  }, [request, onComplete]);

  // QUEUING
  useEffect(() => {
    const el = mainDomRef.current;
    if (!el || status !== "queuing") return;
    const { from, to } = request.info;

    if (from === 5 && to === 4) {
      slideAnimationRef.current = el.animate(
        [
          { transform: `translateX(-3.75rem) scale(0.5)`, opacity: 0 },
          { transform: `translateX(-3.75rem) scale(1)`, opacity: 1 },
        ],
        {
          duration: 400,
          fill: "forwards",
          easing: "ease-in",
        },
      );
    } else if (from === 5) {
      slideAnimationRef.current = el.animate(
        [
          { transform: `translateX(-8rem)` },
          { transform: `translateX(-3.75rem)` },
        ],
        {
          duration: 400,
          fill: "forwards",
        },
      );
    } else {
      slideAnimationRef.current = el.animate(
        [
          {
            transform: `translateX(${from === -1 ? -8 : from * -1.25}rem)`,
          },
          { transform: `translateX(${to * -1.25}rem)` },
        ],
        {
          duration: 400,
          fill: "forwards",
        },
      );
    }

    slideAnimationRef.current.finished
      .then(() => {
        onComplete?.(request);
      })
      .catch(() => {});

    return () => slideAnimationRef.current?.cancel();
  }, [request.id, request.info]);

  // PROCESSING
  useEffect(() => {
    const el = mainDomRef.current;
    if (!el || status !== "server-processing") return;
    processAnimationRef.current = el.animate(
      [{ marginRight: "8rem" }, { marginRight: "0" }],
      {
        duration: 200,
        easing: "ease-out",
        fill: "forwards",
      },
    );

    processAnimationRef.current.finished
      .then(() => {
        onComplete?.(request);
      })
      .catch(() => {});

    return () => processAnimationRef.current?.cancel();
  }, [request, onComplete]);

  // PROCESSED
  useEffect(() => {
    const el = mainDomRef.current;
    if (!el || status !== "server-processed") return;
    slideAnimationRef.current = el.animate(
      [{ right: 0 }, { right: "calc(100% - 4rem)" }],
      {
        duration: 500,
        easing: "linear",
        fill: "forwards",
      },
    );

    slideAnimationRef.current.finished
      .then(() => {
        selfDestruct?.(request);
      })
      .catch(() => {});

    return () => slideAnimationRef.current?.cancel();
  }, [request, onComplete]);

  // REJECTED
  useEffect(() => {
    const el = mainDomRef.current;
    if (!el || !["server-rejected", "queue-rejected"].includes(status)) return;
    processAnimationRef.current = el.animate(
      [
        { transform: "translate(0, 0)", offset: 0 },
        {
          transform: "translate(-0.5rem, 0)",
          offset: 0.2,
        },
        {
          transform: "translate(-1rem, 2rem)",
          opacity: 0,
          offset: 1,
        },
      ],
      {
        duration: 750,
        easing: "ease-out",
        fill: "forwards",
      },
    );

    processAnimationRef.current.finished
      .then(() => {
        selfDestruct?.();
      })
      .catch(() => {});

    return () => processAnimationRef.current?.cancel();
  }, [request, onComplete]);

  return (
    <div
      ref={mainDomRef}
      className={`${css.main} ${css[status] || ""}`}
      data-phase={phase}
      style={style}
    >
      <div
        ref={circleDomRef}
        className={`${css.circle} ${timedOut ? css.timedOut : ""}`}
      >
        {timeoutSeconds &&
          !timedOut &&
          !["queue-rejected", "server-rejected"].includes(status) && (
            <div ref={fillDomRef} className={css.fill} />
          )}
        <div ref={radialDomRef} className={css.radialWipe} />
      </div>
    </div>
  );
};

export default Request;
